import { useQuery } from '@frontend/react-query-helpers';
import { SchemaIntegrationsService } from '@frontend/schema';
import { documentCenterSupportedPMS, supportedPMSForInsuranceWriteback } from './config';
import { ClickToOpenSupportedHookResponse, WritebackSupportedHookResponse } from './types';

const queryKeys = {
  dataSourceDetails: (locationId: string, dataSourceId?: string) => [
    locationId,
    'integrations',
    dataSourceId,
    'datasource-details',
  ],
};

interface GetDataSourceDetailsParams {
  locationId: string;
  sourceId?: string;
  disabled?: boolean;
}

export const useGetDataSourceDetails = ({ locationId, sourceId, disabled }: GetDataSourceDetailsParams) => {
  return useQuery(
    queryKeys.dataSourceDetails(locationId, sourceId ?? ''),
    () => SchemaIntegrationsService.GetDataSource({ locationId, sourceId: sourceId }),
    {
      cacheTime: 8 * 3.6e6, // Adding stale time as 8 hr since the data is expected to be static for the entire day
      staleTime: 8 * 3.6e6, // Adding cache time as 8 hr since the data is expected to be static for the entire day
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: Boolean(locationId && sourceId && !disabled),
    }
  );
};

export const useIsWritebackSupported = ({
  locationId,
  sourceId,
  disabled,
}: GetDataSourceDetailsParams): WritebackSupportedHookResponse => {
  const { data, isLoading } = useGetDataSourceDetails({ locationId, sourceId, disabled });

  if (!sourceId || isLoading) {
    return {
      isLoading,
      PMSName: '',
      isInsuranceDetailsWritebackSupported: false,
      isDocumentCenterWritebackSupported: false,
    };
  }

  const PMSName = data?.integration?.displayName ?? '';
  const comparePMSName = PMSName.toLowerCase().replace(/\s/g, '') ?? '';

  const hasInsuranceDetailsSupportedPMS =
    !!comparePMSName && supportedPMSForInsuranceWriteback.some((pms) => comparePMSName.includes(pms));

  const hasDocumentCenterSupportedPMS =
    !!comparePMSName && documentCenterSupportedPMS.some((pms) => comparePMSName.includes(pms));

  return {
    isInsuranceDetailsWritebackSupported: hasInsuranceDetailsSupportedPMS,
    isDocumentCenterWritebackSupported: hasDocumentCenterSupportedPMS,
    PMSName,
    isLoading,
  };
};

export const useIsClickToOpenSupported = ({
  locationId,
  sourceId,
}: GetDataSourceDetailsParams): ClickToOpenSupportedHookResponse => {
  const { data, isLoading } = useGetDataSourceDetails({ locationId, sourceId });

  if (!sourceId || isLoading) {
    return {
      isLoading,
      pmsName: '',
      isClickToOpenSupported: false,
    };
  }

  const PMSName = data?.integration?.displayName ?? '';
  const comparePMSName = PMSName.toLowerCase().replace(/\s/g, '') ?? '';

  return {
    isClickToOpenSupported: !!comparePMSName && comparePMSName === 'dentrixg5',
    pmsName: comparePMSName,
    isLoading,
  };
};
